import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SearchableAthleteSelect from '../SearchableAthleteSelect';
import { BUILDER_ORDER, SECTION_LABELS, SECTION_SOURCE_MODULE, resolvePeriod } from './constants';
import { Lock } from 'lucide-react';

const SHORTCUTS = [
  ['last_month', 'Last Month'],
  ['last_3_months', 'Last 3 Months'],
  ['season_to_date', 'Season to Date'],
  ['custom', 'Custom'],
];

const CreateReportDialog = ({ open, onOpenChange, athletes, hasModuleAccess, isAdmin, seasons = [], onCreate, creating }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [athleteId, setAthleteId] = useState(null);
  const [seasonId, setSeasonId] = useState('');
  const [shortcut, setShortcut] = useState('last_3_months');
  const [start, setStart] = useState(resolvePeriod('last_3_months').start);
  const [end, setEnd] = useState(today);
  const [reportDate, setReportDate] = useState(today);
  const [selected, setSelected] = useState(() => new Set(BUILDER_ORDER));

  const canSource = (key) => {
    const mod = SECTION_SOURCE_MODULE[key];
    if (!mod) return true;
    return isAdmin || hasModuleAccess(mod, 'view');
  };

  const applyShortcut = (sc) => {
    setShortcut(sc);
    if (sc === 'custom') return;
    const r = resolvePeriod(sc);
    setStart(r.start); setEnd(r.end);
  };

  const toggleSection = (key) => {
    if (!canSource(key)) return;
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };

  const validAthlete = useMemo(() => athleteId && (Array.isArray(athleteId) ? athleteId[0] : athleteId), [athleteId]);
  const resolvedAthleteId = Array.isArray(athleteId) ? athleteId[0] : athleteId;

  const submit = () => {
    if (!resolvedAthleteId || start > end) return;
    onCreate({
      athlete_id: resolvedAthleteId,
      season_id: seasonId || null,
      review_start_date: start,
      review_end_date: end,
      report_date: reportDate,
      selected_sections: BUILDER_ORDER.filter((k) => selected.has(k) && canSource(k)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-report-dialog">
        <DialogHeader><DialogTitle>Create Development Report</DialogTitle></DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="mb-1.5 block">Athlete</Label>
            <SearchableAthleteSelect
              athletes={athletes}
              value={athleteId}
              onChange={setAthleteId}
              multi={false}
              placeholder="Search & select an athlete…"
              data-testid="create-athlete-select"
            />
          </div>

          {seasons.length > 0 && (
            <div>
              <Label className="mb-1.5 block">Season (optional)</Label>
              <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="create-season-select">
                <option value="">— No specific season —</option>
                {seasons.map((s) => <option key={s.id} value={s.id}>{s.name || s.label || s.id}</option>)}
              </select>
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">Review Period</Label>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {SHORTCUTS.map(([sc, label]) => (
                <button key={sc} type="button" onClick={() => applyShortcut(sc)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${shortcut === sc ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`period-${sc}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs text-slate-500">Start</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setShortcut('custom'); }} data-testid="period-start" /></div>
              <div><Label className="mb-1 block text-xs text-slate-500">End</Label><Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setShortcut('custom'); }} data-testid="period-end" /></div>
            </div>
            {start > end && <p className="mt-1 text-xs text-rose-600">Start date must be on or before the end date.</p>}
          </div>

          <div>
            <Label className="mb-1 block">Report Date</Label>
            <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="max-w-[200px]" data-testid="report-date" />
          </div>

          <div>
            <Label className="mb-1.5 block">Sections</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {BUILDER_ORDER.map((key) => {
                const sourceable = canSource(key);
                const on = selected.has(key) && sourceable;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!sourceable}
                    onClick={() => toggleSection(key)}
                    title={!sourceable ? "You don't have permission to include this section." : ''}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${!sourceable ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300' : on ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`section-toggle-${key}`}
                  >
                    <span>{SECTION_LABELS[key]}</span>
                    {!sourceable ? <Lock className="h-3.5 w-3.5" /> : <input type="checkbox" readOnly checked={on} className="pointer-events-none" />}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-slate-400">Sections you can't source are locked. Not every section is required.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!validAthlete || start > end || creating} data-testid="create-submit">
            {creating ? 'Creating…' : 'Generate Draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReportDialog;
